import React, { useState, useContext } from 'react';
import {
  Card,
  Button,
  Typography,
  Space,
  Spin,
  Alert,
  Row,
  Col,
  List,
  Statistic,
  Divider,
  Tag,
  Progress,
  message,
  Avatar,
  Popover,
  Pagination,
  Descriptions,
  Modal
} from 'antd';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  BookOutlined,
  CheckCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  LinkedinOutlined,
  CloseCircleOutlined,
  StarFilled
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useEnrolledCourses from 'PlatForms/SelfLMS/Hooks/Courses/useEnrolledCourses';
import { selfLearnerService } from 'PlatForms/SelfLMS/Services/Courses/CourseService';
import { ProfileContext } from 'Common/hooks/context/ProfileContext';

const { Title, Text } = Typography;

const EnrolledCourses = () => {
  const navigate = useNavigate();
  
  // Use the hook with pagination support
  const {
    enrollments,
    loading,
    error,
    pagination,
    handlePageChange,
    handlePageSizeChange,
    refetch
  } = useEnrolledCourses();
  
  const [startingCourse, setStartingCourse] = useState(null); // Track which course is being started
  const [expandedCourseId, setExpandedCourseId] = useState(null); // Track which course description is expanded
  const { isProfileComplete } = useContext(ProfileContext);
  const isBlocked = !isProfileComplete;

  // Price Modal State
      const [isModalVisible, setIsModalVisible] = useState(false);
      const [selectedPriceData, setSelectedPriceData] = useState(null);

      const openPriceModal = (e, course) => {
    e.stopPropagation(); // Prevents navigating to course details
    setSelectedPriceData(course);
    setIsModalVisible(true);
  };
  

  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";

    const [hours, minutes] = timeString.split(":");
    const hoursInt = parseInt(hours);
    const minutesInt = parseInt(minutes);

    if (hoursInt === 0 && minutesInt === 0) return "Not specified";
    if (hoursInt === 0) return `${minutesInt}m`;
    if (minutesInt === 0) return `${hoursInt}h`;

    return `${hoursInt}h ${minutesInt}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not completed";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  // Logic to convert days to readable Y/M/D
  const formatDuration = (totalDays) => {
    if (!totalDays) return "Not specified";
    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const days = (totalDays % 365) % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    return parts.join(", ");
  };

  // Helper function to get author name
  const getAuthorName = (course) => {
    if (course.courseAuthor && course.courseAuthor.name) {
      return course.courseAuthor.name;
    }
    if (course.authorName) {
      return course.authorName;
    }
    return "Unknown Author";
  };

  // Helper function to get LinkedIn URL
  const getLinkedInUrl = (course) => {
    if (course.courseAuthor && course.courseAuthor.linkedinUrl) {
      return course.courseAuthor.linkedinUrl;
    }
    if (course.linkedinUrl) {
      return course.linkedinUrl;
    }
    if (course.authorLinkedIn) {
      return course.authorLinkedIn;
    }
    return null;
  };

  

  // Function to toggle description expansion
  const toggleDescription = (courseId, e) => {
    e.stopPropagation();
    setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
  };


  // Handle pagination change
  const handlePaginationChange = (page, size) => {
    if (size !== pagination.pageSize) {
      handlePageSizeChange(size);
    } else {
      handlePageChange(page);
    }
  };

  const handleContinueLearning = async (enrollmentId) => {
    if (isBlocked) {
      message.warning("Please complete your profile first");
      return;
    }

    try {
      setStartingCourse(enrollmentId);

      const response = await selfLearnerService.startCourseEnrollment(enrollmentId);

      console.log('API response:', response);

      if (response.isAssessmentPage) {
        navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${response.moduleProgressId}/assessment`, {
          state: {
            moduleId: response.moduleId,
            assessmentId: response.assessmentPage.id,
            moduleProgressId: response.moduleProgressId,
            enrollmentId: enrollmentId,
            assessmentTitle: response.assessmentPage.title,
            assessmentData: response
          }
        });
      } else {
        let pageId;

        if (response.currentPageId) {
          pageId = response.currentPageId;
        } else if (response.hasPrevious && response.previousPageId) {
          pageId = response.previousPageId;
        } else if (response.hasNext && response.nextPageId) {
          pageId = response.nextPageId;
        } else {
          pageId = response.moduleId;
          console.warn("No specific page ID found, using module ID as fallback");
        }

        navigate(`/selfLearner-dashboard/enrolled-courses/${enrollmentId}/modules/${response.moduleProgressId}/pages/${pageId}`, {
          state: {
            moduleId: response.moduleId,
            enrollmentId: enrollmentId // Add this to ensure consistency
          }
        });
      }

    } catch (error) {
      console.error('Error starting course:', error);
      message.error('Failed to start course. Please try again.');
    } finally {
      setStartingCourse(null);
    }
  };

  const handleViewCourse = (enrollmentId) => {
    if (isBlocked) {
      message.warning("Please complete your profile first");
      return;
    }

    navigate(`/selfLearner-dashboard/enrolled-courses/courses/${enrollmentId}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Calculate statistics using totalCount from API
  const totalEnrolled = pagination.totalCount || enrollments.length;
  const completedCourses = enrollments.filter(enrollment => enrollment.isCompleted).length;
  const inProgressCourses = totalEnrolled - completedCourses;

  console.log(".............enrollments", enrollments);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Enrolled Courses"
        description={error}
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  return (

   <Card 
         style={{ borderRadius: '16px', border: 'none', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)', overflow: 'hidden', marginBottom: '16px' }}
         bodyStyle={{ padding: '0' }}
       >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* Show incomplete profile alert if profile is not complete */}
        {isBlocked && (
          <Alert
            message="Complete Your Profile"
            description="Please complete your profile to access courses."
            type="warning"
            showIcon
            style={{ marginBottom: '20px' }}
            action={
              <Button
                size="small"
                type="primary"
                onClick={() => navigate("/selfLearner-profile")}
              >
                Complete Profile
              </Button>
            }
            closable
          />
        )}

        <Card
          title="My Enrolled Courses"
          className="card-professional"
          extra={
            <Button type="default" icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Back
            </Button>
          }
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>

            {/* Summary Stats */}
            <Row gutter={[12, 12]} justify="center">
              <Col xs={24} sm={8} md={6}>
                <Card size="small" className="cardContainer" style={{ padding: '12px' }}>
                  <Statistic
                    title={<span style={{ fontSize: 12 }}>Total Enrolled</span>}
                    value={totalEnrolled}
                    prefix={<BookOutlined />}
                    valueStyle={{ color: '#9A6B0E', fontSize: 16 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Card size="small" className="cardContainer" style={{ padding: '12px' }}>
                  <Statistic
                    title={<span style={{ fontSize: 12 }}>In Progress (this page)</span>}
                    value={inProgressCourses}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#faad14', fontSize: 16 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Card size="small" className="cardContainer" style={{ padding: '12px' }}>
                  <Statistic
                    title={<span style={{ fontSize: 12 }}>Completed (this page)</span>}
                    value={completedCourses}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#52c41a', fontSize: 16 }}
                  />
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* Enrolled Courses List */}
            {enrollments.length > 0 ? (
              <>
                <List
                  grid={{
                    gutter: 16,
                    xs: 1,
                    sm: 1,
                    md: 2,
                    lg: 2,
                    xl: 3,
                    xxl: 3,
                  }}
                  dataSource={enrollments}
                  renderItem={(enrollment) => {
                    const linkedInUrl = getLinkedInUrl(enrollment.course);
                    const authorName = getAuthorName(enrollment.course);

                    return (
                      <List.Item key={enrollment.id}>
     <Card
  hoverable={!enrollment.isBlocked && !isBlocked}
  style={{
    height: "100%",
    minHeight: 400,
    display: "flex",
    flexDirection: "column",
    cursor: enrollment.isBlocked || isBlocked ? "not-allowed" : "pointer",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    borderRadius: "12px",
    border: "1px solid #E7E1D5",
  }}
  /* FIX 1: Main Card click now goes to Detail Page, just like your first code */
  onClick={() => !enrollment.isBlocked && !isBlocked && handleViewCourse(enrollment.id)}
  bodyStyle={{
    padding: "16px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  }}
>
  {/* 1. LARGE PRICE BANNER */}
  <div 
    onClick={(e) => {
      e.stopPropagation(); // Prevents navigating to detail page when clicking price
      openPriceModal && openPriceModal(e, enrollment.course);
    }}
    style={{
      position: "absolute",
      top: 22,
      right: -45,
      background: enrollment.course?.isPaid ? "#9A6B0E" : "#52c41a", 
      color: "white",
      padding: "6px 0", 
      transform: "rotate(45deg)",
      fontSize: "13px",
      fontWeight: "bold",
      zIndex: 10,
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      textAlign: "center",
      width: "160px",
      cursor: "pointer",
      lineHeight: "1"
    }}
  >
    {enrollment.course?.isPaid ? (
      `${enrollment.course.pricing?.currency === "USD" ? "$" : (enrollment.course.pricing?.currency || "$")}${enrollment.course.pricing?.price || 0}`
    ) : (
      "FREE"
    )}
  </div>

  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
    <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "16px" }}>
      
      {/* 2. Status Tag and Title */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start", minHeight: "80px" }}>
        <Tag 
          color={enrollment.isCompleted ? "green" : enrollment.isBlocked ? "red" : "gold"} 
          icon={enrollment.isCompleted ? <CheckCircleOutlined /> : enrollment.isBlocked ? <CloseCircleOutlined /> : <ClockCircleOutlined />}
          style={{ margin: 0 }}
        >
          {enrollment.isCompleted ? 'Completed' : enrollment.isBlocked ? 'Blocked' : 'In Progress'}
        </Tag>
        
       <Title
  level={4}
  style={{
    margin: 0,
    paddingRight: 50,
    fontSize: "16px",
    lineHeight: "22px",
    height: "44px", 
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical"
  }}
>
  {enrollment.course?.title || 'Untitled Course'}
  
  {/* Add this expert tag code */}
  {enrollment.course?.hasActiveExpertSupport && (
    <span style={{ 
      marginLeft: '8px', 
      fontSize: '11px', 
      color: '#faad14', 
      display: 'inline-flex', 
      alignItems: 'center',
      verticalAlign: 'middle',
      fontWeight: 'normal',
      background: '#fffbe6',
      padding: '2px 6px',
      borderRadius: '4px',
      border: '1px solid #ffe58f'
    }}>
      <StarFilled style={{ marginRight: '4px' }} />
      Expert Support
    </span>
  )}
</Title>
      </div>

      {/* 3. Author Info */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Avatar size="small" icon={<UserOutlined />} />
        <Popover 
            content={<div style={{ padding: '4px' }}>{authorName} {linkedInUrl && <a href={linkedInUrl} target="_blank" rel="noreferrer"><LinkedinOutlined /></a>}</div>} 
            trigger="click"
        >
            <span 
                style={{ fontSize: "13px", color: "#57534E", cursor: "pointer", textDecoration: "underline dotted" }}
                onClick={(e) => e.stopPropagation()} // Prevents navigating to detail page
            >
                {authorName || "Unknown Author"}
            </span>
        </Popover>
      </div>

      {/* 4. Description Section */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "85px", position: "relative" }}>
        <div style={{ fontSize: '14px', color: '#57534E' }}>
          <div style={{
              overflowY: expandedCourseId === enrollment.id ? "auto" : "hidden",
              display: expandedCourseId === enrollment.id ? "block" : "-webkit-box",
              WebkitLineClamp: expandedCourseId === enrollment.id ? "unset" : 3,
              WebkitBoxOrient: "vertical",
              height: "63px", 
              lineHeight: "21px",
              paddingRight: expandedCourseId === enrollment.id ? "4px" : "0"
            }}
            className="custom-scrollbar"
          >
            {enrollment.course?.description || "No description available."}
          </div>
          
          {enrollment.course?.description?.length > 70 && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: '22px', fontSize: '12px', marginTop: '2px' }}
              onClick={(e) => {
                e.stopPropagation(); // Prevents navigating to detail page
                toggleDescription(enrollment.id, e);
              }}
            >
              {expandedCourseId === enrollment.id ? "See less" : "See more"}
            </Button>
          )}
        </div>
      </div>

      {/* 5. Progress Section */}
      <div style={{ background: '#F6F3EC', padding: '12px', borderRadius: '8px', marginTop: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <Text type="secondary" style={{ fontSize: '11px' }}>Course Progress</Text>
          <Text strong style={{ fontSize: '12px', color: '#9A6B0E' }}>
            {enrollment.isCompleted ? 100 : Math.round(((enrollment.completedModuleCount || 0) / (enrollment.moduleCount || 1)) * 100)}%
          </Text>
        </div>
        <Progress 
          percent={enrollment.isCompleted ? 100 : Math.round(((enrollment.completedModuleCount || 0) / (enrollment.moduleCount || 1)) * 100)} 
          strokeColor={enrollment.isCompleted ? '#52c41a' : enrollment.isBlocked ? '#ff4d4f' : '#9A6B0E'} 
          strokeWidth={4} 
          showInfo={false} 
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
            <Text type="secondary" style={{ fontSize: '10px' }}>
                <BookOutlined /> {enrollment.completedModuleCount || 0}/{enrollment.moduleCount || 0} Modules
            </Text>
            <Text type="secondary" style={{ fontSize: '10px' }}>
                <ClockCircleOutlined /> {formatTime(enrollment.course?.estimatedTime)}
            </Text>
        </div>
      </div>
    </div>

    {/* 6. Footer and Action Button */}
    <div style={{ borderTop: '1px solid #E7E1D5', paddingTop: '12px', marginTop: '16px' }}>
      <div style={{ marginBottom: '12px' }}>
        <Text type="secondary" style={{ fontSize: "11px" }}>
            <CalendarOutlined /> Enrolled: {formatDate(enrollment.createdOn)}
        </Text>
      </div>

      <Button
        type={enrollment.isCompleted || enrollment.isBlocked ? "default" : "primary"}
        icon={<PlayCircleOutlined />}
        onClick={(e) => {
          /* FIX 2: Button specifically handles learning redirection */
          e.stopPropagation(); 
          handleContinueLearning(enrollment.id);
        }}
        loading={startingCourse === enrollment.id}
        disabled={enrollment.isBlocked || isBlocked}
        block
        style={{ height: '38px', borderRadius: '6px' }}
      >
        {enrollment.isCompleted ? "Review Course" : enrollment.isBlocked ? "Course Blocked" : "Continue Learning"}
      </Button>
    </div>
  </div>
</Card>
                      </List.Item>
                    );
                  }}
                />

                {/* Pagination Component */}
                {enrollments.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <Pagination
                      current={pagination.currentPage}
                      pageSize={pagination.pageSize}
                      total={pagination.totalCount}
                      onChange={handlePaginationChange}
                      onShowSizeChange={handlePaginationChange}
                      showSizeChanger
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} courses`}
                      pageSizeOptions={['6', '9', '12', '18', '24']}
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <BookOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#57534E' }}>
                  No Enrolled Courses
                </Title>
                <Text style={{ color: '#57534E' }}>
                  You have not enrolled in any courses yet.
                </Text>
                <div style={{ marginTop: '16px' }}>
                  <Button
                    type="primary"
                    onClick={() => navigate('/selfLearner-dashboard/available-courses')}
                    disabled={isBlocked}
                  >
                    Browse Available Courses
                  </Button>
                </div>
              </div>
            )}
          </Space>
        </Card>
      </div>
        {/* PRICE DETAIL MODAL */}
            <Modal
              title="Pricing & Access Information"
              open={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              footer={[<Button key="close" type="primary" onClick={() => setIsModalVisible(false)}>Got it</Button>]}
            >
              {selectedPriceData && (
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Price">
                    <Text strong type="success" style={{ fontSize: '16px' }}>
                      {selectedPriceData.pricing?.currency} {selectedPriceData.pricing?.price}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Access Duration">
                    {formatDuration(selectedPriceData.pricing?.accessDurationDays)}
                  </Descriptions.Item>
           
                </Descriptions>
              )}
            </Modal>
    </Card>
    
  );
};

export default EnrolledCourses;